import React, { useEffect, useMemo, useState, useRef } from "react";
import Slider from "../../../components/ui/Slider";
import Wrapper from "../../../components/helpers/Wrapper"
import PageHeader from "../../../components/layout/pageHeader"
import SelectedGlobalFilter from "../../../components/ui/SelectedGlobalFilter"
import WrapperCard from "../../../components/ui/card/card";
import useApi from "../../../hooks/useApi";
import Card from "../../../components/ui/Card";
import { createErrorLoaderSection, createLoaderSection, createNoDataLoaderSection } from "../../../components/ui/states/States";
import { SwiperSlide } from "swiper/react";
import NoDataAvailableChecker from "../../../utils/NoDataAvailableChecker";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import Dropdown from "../../../components/ui/dropdown/Dropdown";
import ExportDropdown from "../../../components/ui/dropdown/ExportDropdown";
import Pie from "../../../components/ui/charts/Pie"
import BarChart from "../../../components/ui/charts/BarChart";
import useFetch from "../../../hooks/useFetch";
import StackedColumnChart from "../../../components/ui/charts/StackedColumnChart";
import Loader from "../../../components/ui/loaders/Loader";
import Button from "../../../components/ui/Button";
import Datatable from "../../../components/ui/tables/Datatable";
import SummaryDatatable from "../../../components/ui/tables/SummaryDatatable";

import { getUniqueValuesFromArrofObj } from "../../../utils/Utils";
import WaterFallChart from "../../../components/ui/charts/WaterFallChart";
import BubbleChartSizeShapeDynamic from "../../../components/ui/charts/BubbleChartSizeShapeDynamic";
import CombinedBarLineMultipleAxesChart from "../../../components/ui/charts/CombinedBarLineMultipleAxesChart";
import CompareBarLineChart from "../../../components/ui/charts/CompareBarLineChart";

import HeatRiskGrowth from "../../../components/ui/charts/HeatRiskGrowth";
import { getUniqueXY } from "../../../utils/table";
import SimpleTreeMap from "../../../components/ui/charts/SimpleTreeMap";
import CustomBarChart from "../../../components/ui/charts/CustomBarChart";
import PremiumIndexChart from "../../../components/ui/charts/PremiumIndexChart";
import { useSelector } from "react-redux";
import SortOrderButton from "../../../components/ui/dropdown/SortOrderButton";
import { ReactComponent as TableIcon } from "../../../assets/icons/icons-svg/Table-icon.svg";
import { ReactComponent as ChartIcon } from "../../../assets/icons/icons-svg/Chart-icon.svg";

import CompareAnalysis from "../../../components/compare/CompareAnalysis";
import MapComponent from '../../../pageComponents/MapComponent';

const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    category: "category_id",
    sub_category: "sub_category_id",
    segment: "segment_id",
    form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    variant: "variant_id",
    pack_size: "pack_size_id",
    promotion: "promotion_id",
    sku: "sku_id",
};


const SummaryTable = () => {
    const [rawTableData, setRawTableData] = useState({
        results: [],
        label: [],
        page: {}
    });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });

    const { post, isLoading, error } = useFetch();
    const api_version = "-v1";

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Memoize payload using Redux state for dates and global filters
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {}
        // No pagination here: we want to fetch all for client-side logic
    }), [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // Fetch the full data set on mount or when date/global filter changes
    useEffect(() => {
        const fetchData = async () => {
            const response = await post(`ri/sales/sales-summary${api_version}`, payload);

            if (response) {
                const { data = [], label = [], pagination = {} } = response;
                setRawTableData({
                    results: data,
                    label,
                    page: pagination
                });
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [payload]);

    // --- Client-side: filter, sort, paginate ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Utility loaders (implement or import as needed)
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    // Handler for Datatable pagination/search/sort
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    let tableContent = null;
    if (isLoading) {
        tableContent = <Loader loaderType="smallTableLoader" />;
    } else if (error) {
        tableContent = createErrorLoaderSection('smallTableLoader');
    } else if (!isLoading && (!rawTableData || rawTableData.results.length === 0)) {
        tableContent = createNoDataLoaderSection('smallTableLoader');
    } else {
        tableContent = (
            <SummaryDatatable
                tableTitle={"Summary"}
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                showPagination={true}
                searchPlaceHolder="Search here..."
                rawTableData={filtered}
            // showGrowthDegrowth={true}   // <--- ADD THIS LINE
            />
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {tableContent}
            </ErrorBoundary>
        </Wrapper>
    );
};

const SalesDeepDiveAnalysis = () => {
    const api_version = "-v1";
    const [toggleTable, setToggleTable] = useState(false);
    const [breadcrumb, setBreadcrumb] = useState(["HOME"]);
    const [level, setLevel] = useState(1);
    const [chart, setChart] = useState({ data: [], label: [] });
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });

    const { post, isLoading, error } = useFetch();

    const levelKeys = [
        "country", "store_type", "category", "sub_category", "segment",
        "form", "manufacture", "brand", "variant", "pack_size",
        "promotion", "sku"
    ];

    // Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Keep level in sync with breadcrumb
    useEffect(() => {
        setLevel(breadcrumb.length);
    }, [breadcrumb]);

    // Memoized chart_filter and section_filter, payload
    const chart_filter = useMemo(() =>
        breadcrumb.slice(1).reduce((acc, label, idx) => {
            const key = levelKeys[idx];
            if (key && label) acc[key] = label;
            return acc;
        }, {}), [breadcrumb]
    );

    const section_filter = useMemo(() => ({
        level: breadcrumb.length,
        chart_filter: Object.keys(chart_filter).length ? chart_filter : undefined
    }), [breadcrumb, chart_filter]);

    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter
    }), [
        startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate,
        section_filter, apiGlobalFilters
    ]);

    // Clean data by converting null/undefined values to empty strings
    const cleanData = (data) =>
        data.map(item =>
            Object.fromEntries(
                Object.entries(item).map(([k, v]) => [k, v == null ? "" : v])
            )
        );

    // Fetch all table data at once for client-side logic
    const fetchTableData = async () => {
        try {
            const res = await post(
                `ri/sales/sales-sales-deep-dive-analysis-table${api_version}`,
                payload
            );
            const { data = [], label = [], pagination = {} } = res;
            setRawTableData({
                results: cleanData(data),
                label,
                page: pagination
            });
        } catch {
            setRawTableData({ results: [], label: [], page: {} });
        }
    };

    const fetchChartData = async () => {
        try {
            const res = await post(
                `ri/sales/sales-sales-deep-dive-analysis${api_version}`,
                payload
            );
            setChart({
                data: res?.data || [],
                label: res?.label || []
            });
        } catch {
            setChart({ data: [], label: [] });
        }
    };

    // Always fetch data on mount and when dependencies change
    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [toggleTable, breadcrumb, payload]);

    const handleTableConfigs = (newConfig) => setPaginationConfig(prev => ({ ...prev, ...newConfig }));
    const MAX_LEVEL = 12;

    const onDrill = (label) => {
        if (breadcrumb.length >= MAX_LEVEL) {
            alert("No further drill down available.");
            return;
        }
        if (label || breadcrumb[breadcrumb.length - 1] !== label) {

            setBreadcrumb((bc) => [...bc, label]);
            // setLevel handled by breadcrumb effect
        }
    };

    const onCrumbClick = (i) => {
        if (i < breadcrumb.length - 1) {
            setBreadcrumb((bc) => bc.slice(0, i + 1));
            // setLevel will be handled by breadcrumb useEffect
            setPaginationConfig(cfg => ({ ...cfg, page_num: 1, search_value: "" }));
        }
    };

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const paginatedData = filtered.slice(startIdx, startIdx + pageSize);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const renderChart = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection("stackedColumnChart");
        if (!chart.data.length || NoDataAvailableChecker(chart.data)) {
            return createNoDataLoaderSection("stackedColumnChart");
        }
        {/*
<Wrapper>
    <ErrorBoundary chartType="treeChartType">
        <SimpleTreeMap
            chartId="simpleTreemaptest"
            chartClass="section-card-chart"
            chartData={chart.data}
            chartLabels={chart.label}
            colorChangeParameter="growth_percentage"
            positiveNegativeNeutral={true}
            levelHandler={(node) => {
                const label = node?.label || node?.name || "";
                onDrill(label);
            }}
        />
    </ErrorBoundary>

    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 24,
        gap: 32,
        flexWrap: 'wrap'
    }}>
        <span style={{ color: "#213345", fontSize: 12, marginRight: 24 }}>
            Area = Market Share by Sales Value %
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
                display: "inline-block",
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                background: '#06a57d',
                marginRight: 3
            }} />
            <span style={{ color: "#213345", fontSize: 12 }}>
                Sales Value % Growth
            </span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
                display: "inline-block",
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                background: '#b51c2a',
                marginRight: 3
            }} />
            <span style={{ color: "#213345", fontSize: 12 }}>
                Sales Value % Degrowth
            </span>
        </span>
    </div>
</Wrapper>
*/}


        return (

            <Wrapper>
                <ErrorBoundary chartType="treeChartType">
                    {/* Flex column for chart + legend */}
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", // centers horizontally
                        width: "100%",
                    }}>
                        <SimpleTreeMap
                            chartId="simpleTreemaptest"
                            chartClass="section-card-chart"
                            chartData={chart.data}
                            chartLabels={chart.label}
                            colorChangeParameter="growth_percentage"
                            positiveNegativeNeutral={true}
                            levelHandler={(node) => {
                                const label = node?.label || node?.name || "";
                                onDrill(label);
                            }}
                        />
                        {/* Legend - render only ONCE here! */}
                        {/* <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 24,
          gap: 32,
          flexWrap: 'wrap'
        }}>
          <span style={{ color: "#213345", fontSize: 12, marginRight: 24 }}>
            Area = Market Share by Sales Value %
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              display: "inline-block",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              background: '#06a57d',
              marginRight: 3
            }} />
            <span style={{ color: "#213345", fontSize: 12 }}>
              Sales Value % Growth
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              display: "inline-block",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              background: '#b51c2a',
              marginRight: 3
            }} />
            <span style={{ color: "#213345", fontSize: 12 }}>
              Sales Value % Degrowth
            </span>
          </span>
        </div> */}
                    </div>
                </ErrorBoundary>
            </Wrapper>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Sales Deep-Dive Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="sales_deep_dive" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    {/* Header */}
                    <div className="grid col_span_4">
                        <div className="section_card_header flex justify-between items-center">
                            <div>
                                <h2 className="section_card_title">Sales Deep-Dive Analysis</h2>
                                <div style={{
                                    fontSize: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontFamily: 'Ubuntu, sans-serif',
                                    whiteSpace: 'nowrap',
                                    gap: 0,
                                    marginTop: '16px'
                                }}>
                                    {breadcrumb.map((crumb, idx) => (
                                        <span key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                                            <span
                                                onClick={() => idx < breadcrumb.length - 1 && onCrumbClick(idx)}
                                                style={{
                                                    color: idx === breadcrumb.length - 1 ? '#979797' : '#11A1FD',
                                                    fontWeight:
                                                        idx === 0
                                                            ? 700
                                                            : idx === breadcrumb.length - 1
                                                                ? 400
                                                                : 700,
                                                    cursor: idx < breadcrumb.length - 1 ? 'pointer' : 'default',
                                                    textDecoration: 'none',
                                                    marginRight: 4,
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                {idx === 0 ? <strong>{crumb}</strong> : crumb}
                                            </span>
                                            {idx < breadcrumb.length - 1 && (
                                                <span style={{ color: '#979797', margin: '0 4px', fontWeight: 400 }}>/</span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div data-html2canvas-ignore={true}>
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown
                                    sectionId="sales_deep_dive"
                                    sectionName="Sales Deep Dive"
                                    sectionData={toggleTable ? rawTableData.results : chart.data}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            {toggleTable ? renderTable() : renderChart()}
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const PriceImpactAnalysis = () => {
    const api_version = "-v1";

    const [toggleTable, setToggleTable] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");

    const [rawTableData, setRawTableData] = useState({
        results: [],
        label: [],
        page: {}
    });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });
    const [chart, setChart] = useState({ data: [], label: [] });
    const { post, isLoading, error } = useFetch();

    const dimensionOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];

    const [selectedDimension, setSelectedDimension] = useState(dimensionOptions[7]);

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Memoize payload with Redux dates and filters
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            dimension_dropdown: selectedDimension.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        selectedDimension,
        sortOrder,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // Clean data by converting null/undefined values to empty strings
    const cleanData = (data) => {
        return data.map(item => {
            const cleanedItem = {};
            Object.keys(item).forEach(key => {
                cleanedItem[key] = item[key] === null || item[key] === undefined ? '' : item[key];
            });
            return cleanedItem;
        });
    };

    // Toggle sort order function
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Fetch the full table data for client-side sort/search/pagination
    const fetchTableData = async () => {
        try {
            const response = await post(`ri/sales/sales-price-impact-analysis-table${api_version}`, payload);
            if (response) {
                const { data = [], label = [], pagination = {} } = response;
                setRawTableData({
                    results: cleanData(data),
                    label,
                    page: pagination
                });
            }
        } catch (err) {
            setRawTableData({ results: [], label: [], page: {} });
        }
    };

    // Fetch chart data as before
    const fetchChartData = async () => {
        try {
            const response = await post(`ri/sales/sales-price-impact-analysis${api_version}`, payload);
            setChart(response);
        } catch (err) {
            setChart({ data: [], label: [] });
        }
    };

    // Refetch table/chart on dropdown or toggleTable change
    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Utility loaders (implement or import as needed)
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: false,
                grid: { display: false }
            },
            y: {
                stacked: false,
                beginAtZero: true,
                min: 0,
                ticks: {
                    callback: function (value) {
                        return value + '%';
                    }
                },
                grid: {
                    drawBorder: false,
                    color: function (context) {
                        return context.tick.value === 0 ? '#666' : '#eee';
                    },
                    lineWidth: function (context) {
                        return context.tick.value === 0 ? 2 : 1;
                    }
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    font: { size: 12 }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.raw;
                        const formattedValue = Math.abs(value) > 1000
                            ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
                            : value.toFixed(2);
                        return `${label}: ${formattedValue}%`;
                    }
                },
                displayColors: true,
                backgroundColor: 'rgba(0,0,0,0.8)',
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
                padding: 10
            }
        },
        elements: {
            bar: {
                backgroundColor: function (context) {
                    const value = context.raw;
                    return value < 0
                        ? 'rgba(255, 99, 132, 0.8)'
                        : 'rgba(54, 162, 235, 0.8)';
                },
                borderColor: function (context) {
                    const value = context.raw;
                    return value < 0
                        ? 'rgba(255, 99, 132, 1)'
                        : 'rgba(54, 162, 235, 1)';
                },
                borderWidth: 1,
                borderRadius: 4,
                minBarLength: 2
            }
        }
    };

    const transformChartData = (data) => {
        if (!data || !data.data) return data;
        return {
            ...data,
            data: data.data.map(item => ({
                ...item,
                percentage_change_in_average_price_per_unit: Number(item.percentage_change_in_average_price_per_unit),
                percentage_change_in_sales_value: Number(item.percentage_change_in_sales_value),
                percentage_change_in_sales_unit: Number(item.percentage_change_in_sales_unit)
            }))
        };
    };

    const chartContent = isLoading ? (
        <Loader loaderType="stackedColumnChartLoader" />
    ) : error ? (
        createErrorLoaderSection("stackedColumnChart")
    ) : !chart?.data?.length || NoDataAvailableChecker(chart) ? (
        createNoDataLoaderSection("stackedColumnChart")
    ) : (
        <Wrapper>
            <ErrorBoundary>
                <div style={{ minHeight: 320, height: "340px", position: 'relative' }}>
                    <BarChart
                        chartClass="section-chart-wrapper"
                        chartData={transformChartData(chart).data}
                        chartLabels={chart.label}
                        chartId={"priceImpactChart-" + Math.random() * 1000}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        isHorizontal={false}
                        options={chartOptions}
                        variant="centeredLegend"
                    />
                </div>
            </ErrorBoundary>
        </Wrapper>
    );

    const tableContent = isLoading ? (
        <Loader loaderType="smallTableLoader" />
    ) : error ? (
        createErrorLoaderSection("smallTableLoader")
    ) : !rawTableData?.results?.length || NoDataAvailableChecker(rawTableData.results) ? (
        createNoDataLoaderSection("smallTableLoader")
    ) : (
        <Datatable
            tableTitle="Price Impact Analysis"
            tableData={paginatedData}
            tableHeader={rawTableData.label}
            tableLength={pageSize}
            tableConfigs={paginationConfig}
            setTableConfigs={handleTableConfigs}
            hideBoxShadow={true}
            tablePaginationData={{
                page_number: pageNum,
                total_pages: totalPages,
                total_records: totalRecords,
                record_end: Math.min(pageNum * pageSize, totalRecords)
            }}
            searchPlaceHolder="Search here..."
            showPagination={true}
            error={error}
            emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
        />
    );

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="price_impact_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Price Impact Analysis</h2>
                            <div data-html2canvas-ignore={true}>
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionOptions}
                                    className="grid_margin_right"
                                    value={selectedDimension}
                                    selectedOptions={selectedDimension}
                                    setStatedropdown={setSelectedDimension}
                                />
                                <SortOrderButton
                                    sortOrder={sortOrder}
                                    toggleSortOrder={toggleSortOrder}
                                    // className="grid_margin_right"
                                    className="section_card_header_sort_btn"

                                />
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown
                                    sectionId="price_impact_analysis_section"
                                    sectionName="Price Impact Analysis"
                                    sectionData={toggleTable ? rawTableData.results : chart.data}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? tableContent : chartContent}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};


const PromotionEffectiveAnalysis = () => {
    const api_version = '-v1';

    const dimensionOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];

    const [selectedDimension, setSelectedDimension] = useState(dimensionOptions[0]);
    const [chart, setChart] = useState({
        chartData: [],
        chartLabels: [{
            barchartLabels: [
                { label: "Promotion Sales", value: "promotion_sales" },
                { label: "Regular Sales", value: "regular_sales" }
            ],
            linechartLabels: [
                { label: "Promotion Effectiveness", value: "promotion_effectiveness" }
            ]
        }]
    });

    // --- FOR CLIENT SIDE TABLE LOGIC ---
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });
    const [sortOrder, setSortOrder] = useState('desc');
    const [toggleTable, setToggleTable] = useState(false);

    const { post, isLoading, error } = useFetch();

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Memoized payload using Redux date values and global filters
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            dimension_dropdown: selectedDimension.value,
            top_bottom: { sort_by: sortOrder, size: 5 }
        }
    }), [
        selectedDimension,
        sortOrder,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // Toggle sort order
    const toggleSortOrder = () => {
        const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        setSortOrder(newOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newOrder
        }));
    };

    // Fetch table data (all at once for client side logic)
    const fetchTableData = async () => {
        const response = await post(
            `/ri/sales/sales-promotion-effective-analysis-table${api_version}`,
            payload
        );
        // Use .data, .label, .pagination as in your API response
        const results = response.data || [];
        const label = response.label || [];
        const page = response.pagination || {};
        setRawTableData({ results, label, page });
    };

    const fetchChartData = async () => {
        const response = await post(
            `/ri/sales/sales-promotion-effective-analysis${api_version}`,
            payload
        );
        setChart({ chartData: response.data || [], chartLabels: chart.chartLabels });
    };

    useEffect(() => {
        toggleTable ? fetchTableData() : fetchChartData();
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart.chartData.length || NoDataAvailableChecker(chart.chartData)) return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <CombinedBarLineMultipleAxesChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.chartData}
                    chartLabels={chart.chartLabels}
                    chartId={`promotionEffectivenessChart-${Math.random()}`}
                    showLabels
                    showLegend
                    tooltipMaxWidth="auto"
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Promotion Effectiveness Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="promotion_effective_analysis" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Promotion Effectiveness Analysis</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Dimension"
                                options={dimensionOptions}
                                value={selectedDimension}
                                selectedOptions={selectedDimension}
                                setStatedropdown={setSelectedDimension}
                                className="grid_margin_right"
                            />
                            <SortOrderButton sortOrder={sortOrder}
                                toggleSortOrder={toggleSortOrder}
                                //  className="grid_margin_right"
                                className="section_card_header_sort_btn"

                            />
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown sectionId="promotion_effective_analysis" sectionName="Promotion Analysis" sectionData={toggleTable ? filtered : chart.chartData} />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};


const CategoryDeepDiveAnalysis = () => {
    const api_version = "-v1";

    const [toggleTable, setToggleTable] = useState(false);
    const [rawTableData, setRawTableData] = useState({
        results: [],
        label: [],
        page: {}
    });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });
    const [chart, setChart] = useState({ data: [], label: [] });

    const { post, isLoading, error } = useFetch();

    const valueDropdownOptions = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Units", value: "sales_unit" },
        { label: "PPU", value: "ppu" },
        { label: "PPL", value: "ppl" },
        // { label: "TDP", value: "tdp" },
        // { label: "NumDTotal", value: "numdtotal" },
        // { label: "WtDTotal", value: "wtddtotal" },
        // { label: "Market Share by sales value", value: "market_share_by_sales_value" },
        // { label: "Market Share by Volume", value: "market_share_by_sales_volume" },
        // { label: "Promotional Sales Value%", value: "promotional_sales_value" },
        // { label: "Promotional Sales Volume%", value: "promotional_sales_volume" },
        // { label: "ROSVal", value: "valros" },
        // { label: "ROSVol", value: "volros" },
        // { label: "Number of SKUs", value: "no_of_sku" },
    ];

    const dimensionDropdownOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" },
    ];

    const [valueDropdown, setValueDropdown] = useState(valueDropdownOptions[0]);
    const [valueDropdown1, setValueDropdown1] = useState(valueDropdownOptions[1]);
    const [dimensionDropdown, setDimensionDropdown] = useState(dimensionDropdownOptions[7]);

    // Prevent both value dropdowns from being the same
    const filteredValueDropdownOptions = useMemo(
        () => valueDropdownOptions.filter(opt => opt.value !== valueDropdown1.value),
        [valueDropdown1.value]
    );
    const filteredValueDropdownOptions1 = useMemo(
        () => valueDropdownOptions.filter(opt => opt.value !== valueDropdown.value),
        [valueDropdown.value]
    );

    useEffect(() => {
        if (valueDropdown.value === valueDropdown1.value) {
            const nextValue = valueDropdownOptions.find(opt => opt.value !== valueDropdown.value);
            if (nextValue) setValueDropdown1(nextValue);
        }
        // eslint-disable-next-line
    }, [valueDropdown]);
    useEffect(() => {
        if (valueDropdown.value === valueDropdown1.value) {
            const nextValue = valueDropdownOptions.find(opt => opt.value !== valueDropdown1.value);
            if (nextValue) setValueDropdown(nextValue);
        }
        // eslint-disable-next-line
    }, [valueDropdown1]);

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Use those dates and filters in your payload (useMemo to optimize re-renders)
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: valueDropdown.value,
            value_dropdown1: valueDropdown1.value,
            dimension_dropdown: dimensionDropdown.value
        }
    }), [
        startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate,
        valueDropdown, valueDropdown1, dimensionDropdown, apiGlobalFilters
    ]);

    // Clean data by converting null/undefined values to empty strings
    const cleanData = (data) => {
        return data.map(item => {
            const cleanedItem = {};
            Object.keys(item).forEach(key => {
                cleanedItem[key] = item[key] === null || item[key] === undefined ? '' : item[key];
            });
            return cleanedItem;
        });
    };

    // Fetch the full table data for client-side sort/search/pagination
    const fetchTableData = async () => {
        try {
            const response = await post(
                `ri/sales/sales-category-deep-dive-analysis-table${api_version}`,
                payload
            );
            if (response) {
                setRawTableData({
                    results: cleanData(response.data || []),
                    label: response.label || [],
                    page: response.pagination || {}
                });
            }
        } catch (err) {
            setRawTableData({ results: [], label: [], page: {} });
        }
    };

    // Fetch chart data as before
    const fetchChartData = async () => {
        try {
            const response = await post(
                `ri/sales/sales-category-deep-dive-analysis${api_version}`,
                payload
            );
            if (response) {
                setChart({
                    data: cleanData(response.data || []),
                    label: response.label || []
                });
            }
        } catch (err) {
            setChart({ data: [], label: [] });
        }
    };

    // Refetch table/chart on dropdown or toggleTable change
    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    // Handler for Datatable pagination/search/sort
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );

    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    let chartContent;
    if (isLoading) {
        chartContent = <Loader loaderType="stackedColumnChartLoader" />;
    } else if (error) {
        chartContent = createErrorLoaderSection("stackedColumnChart");
    } else if (!isLoading && (!chart || !chart.data || chart.data.length === 0 || NoDataAvailableChecker(chart.data))) {
        chartContent = createNoDataLoaderSection("stackedColumnChart");
    } else {
        chartContent = (
            <Wrapper>
                <ErrorBoundary>
                    <BubbleChartSizeShapeDynamic
                        chartClass="section-chart-wrapper"
                        chartData={chart.data}
                        chartLabels={chart.label}
                        chartId={"stackedColumnChart-" + Math.random() * 1000}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        valueDropdown={valueDropdown}
                        valueDropdown1={valueDropdown1}
                        dimensionDropdown={dimensionDropdown}
                    />
                </ErrorBoundary>
            </Wrapper>
        );
    }

    let tableContent = null;
    if (isLoading) {
        tableContent = <Loader loaderType="smallTableLoader" />;
    } else if (error) {
        tableContent = createErrorLoaderSection("smallTableLoader");
    } else if (!isLoading && (!rawTableData || rawTableData.results.length === 0 || NoDataAvailableChecker(rawTableData.results))) {
        tableContent = createNoDataLoaderSection("smallTableLoader");
    } else if (!isLoading && rawTableData.results.length > 0) {
        tableContent = (
            <Datatable
                tableTitle="Category Deep Dive Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="category_deep_dive_analysis" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Category Deep Dive Analysis</h2>
                            <div data-html2canvas-ignore={true}>
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionDropdownOptions}
                                    className="grid_margin_right"
                                    value={dimensionDropdown}
                                    selectedOptions={dimensionDropdown}
                                    setStatedropdown={setDimensionDropdown}
                                />
                                <Dropdown
                                    placeholder="Value"
                                    options={filteredValueDropdownOptions}
                                    className="grid_margin_right"
                                    value={valueDropdown}
                                    selectedOptions={valueDropdown}
                                    setStatedropdown={setValueDropdown}
                                />
                                <Dropdown
                                    placeholder="Value 1"
                                    options={filteredValueDropdownOptions1}
                                    className="grid_margin_right"
                                    value={valueDropdown1}
                                    selectedOptions={valueDropdown1}
                                    setStatedropdown={setValueDropdown1}
                                />
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown
                                    sectionId="category_deep_dive_analysis"
                                    sectionName="Category Deep Dive Analysis"
                                    sectionData={toggleTable ? rawTableData.results : chart.data}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? tableContent : chartContent}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const MarketShareGrowth = () => {
    const [toggleTable, setToggleTable] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");

    // CLIENT-SIDE PAGINATION STATE
    const [allTableData, setAllTableData] = useState([]);
    const [tableLabel, setTableLabel] = useState([]);
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });

    // Chart state
    const [chart, setChart] = useState({
        data: [],
        x: { label: "", values: [] },
        y: { label: "", values: [] }
    });
    const { post, isLoading, error } = useFetch();

    const api_version = "-v1";

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    const valueDropdown = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Unit", value: "sales_unit" }
    ];
    const [valueFilter, setValueFilter] = useState(valueDropdown[0]);

    const dimensionDropdown = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];

    // Validation: dimensionDropdown and dimensionDropdown1 must never be the same value
    const [dimensionFilter, setDimensionFilter] = useState(dimensionDropdown[1]);
    const [dimensionFilter1, setDimensionFilter1] = useState(dimensionDropdown[2]);

    // Filter options so that dimensionDropdown and dimensionDropdown1 do not overlap
    const filteredDimensionDropdown = useMemo(
        () => dimensionDropdown.filter(opt => opt.value !== dimensionFilter1.value),
        [dimensionFilter1.value]
    );
    const filteredDimensionDropdown1 = useMemo(
        () => dimensionDropdown.filter(opt => opt.value !== dimensionFilter.value),
        [dimensionFilter.value]
    );

    // Auto-correct if both filters are ever the same
    useEffect(() => {
        if (dimensionFilter.value === dimensionFilter1.value) {
            const nextValue = dimensionDropdown.find(opt => opt.value !== dimensionFilter.value);
            if (nextValue) setDimensionFilter1(nextValue);
        }
        // eslint-disable-next-line
    }, [dimensionFilter]);
    useEffect(() => {
        if (dimensionFilter.value === dimensionFilter1.value) {
            const nextValue = dimensionDropdown.find(opt => opt.value !== dimensionFilter1.value);
            if (nextValue) setDimensionFilter(nextValue);
        }
        // eslint-disable-next-line
    }, [dimensionFilter1]);

    // Toggle sort order function
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
    };

    const cleanData = (data) => {
        if (!data) return [];
        return data.map(item => {
            const cleanedItem = {};
            Object.keys(item).forEach(key => {
                cleanedItem[key] = item[key] === null || item[key] === undefined ? '' : item[key];
            });
            return cleanedItem;
        });
    };

    // --- Fix: derive payload from state via useMemo and use Redux dates and global filters ---
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: valueFilter.value,
            dimension_dropdown: dimensionFilter.value,
            dimension_dropdown1: dimensionFilter1.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate,
        valueFilter, dimensionFilter, dimensionFilter1, sortOrder, apiGlobalFilters
    ]);

    // CLIENT-SIDE TABLE DATA FETCH
    const fetchAllTableData = async () => {
        try {
            const response = await post(`ri/sales/sales-market-share-growth-table${api_version}`, payload);
            if (response) {
                setAllTableData(cleanData(response.data || []));
                setTableLabel(response.label || []);
            }
        } catch (err) {
            setAllTableData([]);
            setTableLabel([]);
        }
    };

    // CHART FETCH
    const fetchChartData = async () => {
        try {
            const response = await post(`ri/sales/sales-market-share-growth${api_version}`, payload);
            if (response) {
                const { uniqueX, uniqueY } = await getUniqueXY(response);
                setChart({
                    data: cleanData(response),
                    x: { label: dimensionFilter.value, values: uniqueX },
                    y: { label: dimensionFilter1.value, values: uniqueY }
                });
            }
        } catch (err) {
            setChart({ data: [], x: { label: "", values: [] }, y: { label: "", values: [] } });
        }
    };

    useEffect(() => {
        if (toggleTable) {
            fetchAllTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // Pagination/search/sort handler for table (client-side)
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    // --- PAGINATION LOGIC FOR CLIENT SIDE ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    // 1. Search
    let filtered = allTableData;
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }

    // 2. Sort (client-side)
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }

    // 3. Paginate
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );

    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart.data.length || NoDataAvailableChecker(chart.data)) return createNoDataLoaderSection('stackedColumnChart');
        return (
            <Wrapper>
                <ErrorBoundary>
                    <HeatRiskGrowth
                        chartId="heatRiskGrowthChart"
                        chartClass="chart-container"
                        yAxisLabels={chart.y}
                        xAxisLabels={chart.x}
                        data={chart.data}
                        valueFieldName={valueFilter.value}
                    />
                </ErrorBoundary>
            </Wrapper>
        );
    };

    // Table rendering (data is paginated, sorted, searched above)
    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Market Share Growth"
                tableData={paginatedData}
                tableHeader={tableLabel}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="market_share_growth_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Market Share Growth</h2>
                            <div data-html2canvas-ignore>
                                <Dropdown
                                    placeholder="Second Dimension"
                                    options={filteredDimensionDropdown1}
                                    className="grid_margin_right"
                                    value={dimensionFilter1}
                                    selectedOptions={dimensionFilter1}
                                    setStatedropdown={setDimensionFilter1}
                                />
                                <Dropdown
                                    placeholder="Dimension"
                                    options={filteredDimensionDropdown}
                                    className="grid_margin_right"
                                    value={dimensionFilter}
                                    selectedOptions={dimensionFilter}
                                    setStatedropdown={setDimensionFilter}
                                />
                                <Dropdown
                                    placeholder="Value"
                                    options={valueDropdown}
                                    className="grid_margin_right"
                                    value={valueFilter}
                                    selectedOptions={valueFilter}
                                    setStatedropdown={setValueFilter}
                                />
                                <SortOrderButton
                                    sortOrder={sortOrder}
                                    toggleSortOrder={toggleSortOrder}
                                    // className="grid_margin_right"
                                    className="section_card_header_sort_btn"

                                />
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown
                                    sectionId="market_share_growth_section"
                                    sectionName="Market Share Growth"
                                    sectionData={toggleTable ? paginatedData : chart.data}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? renderTable() : renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const WaterfallAnalysis = () => {
    const api_version = "-v1";
    const [toggleTable, setToggleTable] = useState(false);
    // CLIENT-SIDE TABLE DATA
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });
    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });

    const { post, isLoading, error } = useFetch();

    const valueDropdown = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Units", value: "sales_unit" },
        { label: "PPU", value: "ppu" },
        { label: "PPL", value: "ppl" },
        { label: "TDP", value: "tdp" },
        { label: "NumDTotal", value: "numdtotal" },
        { label: "WtDTotal", value: "wtddtotal" },
        { label: "Market Share by sales value", value: "market_share_by_sales_value" },
        { label: "Market Share by Volume", value: "market_share_by_sales_volume" },
        { label: "Market Share by Unit", value: "market_share_by_sales_unit" },
        { label: "Promotional Sales Value%", value: "promotional_sales_value" },
        { label: "Promotional Sales Volume%", value: "promotional_sales_volume" },
        { label: "Promotional Sales Unit%", value: "promotional_sales_unit" },
        { label: "Rate of Sale by Value", value: "valros" },
        { label: "Rate of Sale by Volume", value: "volros" },
        { label: "Rate of Sale by Units", value: "unitros" },
        { label: "Number of SKUs", value: "no_of_sku" },
    ];

    const dimensionDropdown = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" },
    ];

    const [metric, setMetric] = useState({ label: "PPU", value: "ppu" });
    const [dimension, setDimension] = useState({ label: "SKU", value: "sku" });

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Use those dates and filters in your payload (useMemo to optimize re-renders)
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: metric.value,
            dimension_dropdown: dimension.value
        }
    }), [
        metric,
        dimension,
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    // Fetch table data (all for client side logic)
    const fetchTableData = async () => {
        const response = await post(`ri/sales/sales-waterfall-analysis-sales-value-table${api_version}`, payload);
        if (response) {
            // Defensive: ensure no nulls in data
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v === null || v === undefined ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    // Chart fetch function
    const fetchChartData = async () => {
        const response = await post(`ri/sales/sales-waterfall-analysis-sales-value${api_version}`, payload);
        setChart(response);
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable, post, api_version]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    let content;
    if (isLoading) {
        content = <Loader loaderType="stackedColumnChartLoader" />;
    } else if (error) {
        content = createErrorLoaderSection('stackedColumnChart');
    } else if (!isLoading && (!chart || chart.length === 0 || NoDataAvailableChecker(chart))) {
        content = createNoDataLoaderSection('stackedColumnChart');
    } else {
        content = (
            <Wrapper>
                <ErrorBoundary>
                    <WaterFallChart
                        chartId="waterfallChart"
                        chartClass="section-chart-wrapper"
                        chartLabels={chart.chartLabels}
                        chartData={chart.chartData}
                        height="350px"
                    />
                </ErrorBoundary>
            </Wrapper>
        );
    }

    let tableContent = null;
    if (isLoading) {
        tableContent = <Loader loaderType="smallTableLoader" />;
    } else if (error) {
        tableContent = createErrorLoaderSection('smallTableLoader');
    } else if (!filtered.length || NoDataAvailableChecker(filtered)) {
        tableContent = createNoDataLoaderSection('smallTableLoader');
    } else if (filtered.length > 0) {
        tableContent = (
            <Datatable
                tableTitle="Waterfall Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="waterfall_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Waterfall Analysis : {metric.label}</h2>
                            <div data-html2canvas-ignore={true}>
                                <Dropdown
                                    placeholder="Metric"
                                    options={valueDropdown}
                                    className="grid_margin_right"
                                    value={metric}
                                    selectedOptions={metric}
                                    setStatedropdown={setMetric}
                                />
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionDropdown}
                                    className="grid_margin_right"
                                    value={dimension}
                                    selectedOptions={dimension}
                                    setStatedropdown={setDimension}
                                />
                                {/* <Button
                                    type="primary"
                                    className="width_fit_content"
                                    handleClick={() => setToggleTable((prev) => !prev)}
                                >
                                    {toggleTable ? "Chart" : "Table"}
                                </Button> */}
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown sectionId="waterfall_analysis_section" sectionName="waterfall analysis" sectionData={toggleTable ? filtered : chart.chartData} />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? tableContent : content}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};


// const TrendComparisonAnalysis = () => {
//     const api_version = "-v1";

//     const [toggleTable, setToggleTable] = useState(false);
//     const [sortOrder, setSortOrder] = useState("desc");
//     // CLIENT-SIDE TABLE DATA
//     const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
//     const [paginationConfig, setPaginationConfig] = useState({
//         page_num: 1,
//         page_size: 10,
//         search_value: "",
//         order_by: "",
//         order_type: "desc"
//     });
//     const [chart, setChart] = useState({ data: [], label: [] });

//     const { post, isLoading, error } = useFetch();

//     const [granularity, setGranularity] = useState({ value: "year", label: "Yearly" });
//     const granularityOptions = [
//         { label: 'Yearly', value: 'year' },
//         { label: 'Quarterly', value: 'quarter_no' },
//         { label: 'Monthly', value: 'month_no' },
//     ];

//     const [metric, setMetric] = useState({ value: "sales_value", label: "Sales Value" });
//     const valueDropdown = [
//         { label: "Sales Value", value: "sales_value" },
//         { label: "Sales Volume", value: "sales_volume" },
//         { label: "Sales Units", value: "sales_unit" },
//         { label: "PPU", value: "ppu" },
//         { label: "PPL", value: "ppl" },
//         { label: "TDP", value: "tdp" },
//         { label: "NumDTotal", value: "numdtotal" },
//         { label: "WtDTotal", value: "wtddtotal" },
//         { label: "Market Share by sales value", value: "market_share_by_sales_value" },
//         { label: "Market Share by Volume", value: "market_share_by_sales_volume" },
//         { label: "Market Share by Unit", value: "market_share_by_sales_unit" },
//         { label: "Promotional Sales Value%", value: "promotional_sales_value" },
//         { label: "Promotional Sales Volume%", value: "promotional_sales_volume" },
//         { label: "Promotional Sales Unit%", value: "promotional_sales_unit" },
//         { label: "Rate of Sale by Value", value: "valros" },
//         { label: "Rate of Sale by Volume", value: "volros" },
//         { label: "Rate of Sale by Units", value: "unitros" },
//         { label: "Number of SKUs", value: "no_of_sku" },
//     ];

//     const [dimension, setDimension] = useState({ value: "country", label: "Country" });
//     const dimensionDropdown = [
//         { label: "Country", value: "country" },
//         { label: "Store Type", value: "store_type" },
//         { label: "Category", value: "category" },
//         { label: "Sub Category", value: "sub_category" },
//         { label: "Segment", value: "segment" },
//         { label: "Form", value: "form" },
//         { label: "Manufacture", value: "manufacture" },
//         { label: "Brand", value: "brand" },
//         { label: "Variant", value: "variant" },
//         { label: "Pack Size", value: "pack_size" },
//         { label: "Promotion", value: "promotion" },
//         { label: "SKU", value: "sku" },
//     ];

//     // 1. Get date values from Redux (DatePicker)
//     const {
//         startDate,
//         endDate,
//         prevStartDate,
//         prevEndDate,
//         secondPrevStartDate,
//         secondPrevEndDate
//     } = useSelector(state => state.date);

//     // 2. Use those dates in your payload (useMemo to optimize re-renders)
//     const payload = useMemo(
//         () => ({
//             global_filter: {
//                 date_filter: {
//                     mat_ty_start_date: startDate,
//                     mat_ty_end_date: endDate,
//                     mat_ly_start_date: prevStartDate,
//                     mat_ly_end_date: prevEndDate,
//                     mat_2y_start_date: secondPrevStartDate,
//                     mat_2y_end_date: secondPrevEndDate,
//                 },
//                 filter: {}
//             },
//             section_filter: {
//                 dimension_dropdown: dimension.value,
//                 granularity_dropdown: granularity.value,
//                 value_dropdown: metric.value,
//                 top_bottom: {
//                     sort_by: sortOrder,
//                     size: 10
//                 }
//             }
//         }),
//         [
//             startDate,
//             endDate,
//             prevStartDate,
//             prevEndDate,
//             secondPrevStartDate,
//             secondPrevEndDate,
//             dimension,
//             granularity,
//             metric,
//             sortOrder
//         ]
//     );

//     // Toggle sort order function
//     const toggleSortOrder = () => {
//         const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
//         setSortOrder(newSortOrder);
//         setPaginationConfig(prev => ({
//             ...prev,
//             order_type: newSortOrder
//         }));
//     };

//     // Fetch table data (all for client side logic)
//     const fetchTableData = async () => {
//         const response = await post(`ri/sales/sales-trend-compare-analysis-table${api_version}`, payload);
//         if (response) {
//             // Defensive: ensure no nulls in data
//             const safeData = (response.data || []).map(row => {
//                 const safeRow = {};
//                 Object.entries(row).forEach(([k, v]) => {
//                     safeRow[k] = v === null || v === undefined ? "" : v;
//                 });
//                 return safeRow;
//             });
//             setRawTableData({
//                 results: safeData,
//                 label: response.label || [],
//                 page: response.pagination || {}
//             });
//         }
//     };

//     // Chart fetch function
//     const fetchChartData = async () => {
//         const response = await post(`ri/sales/sales-trend-compare-analysis${api_version}`, payload);
//         setChart(response);
//     };

//     useEffect(() => {
//         if (toggleTable) {
//             fetchTableData();
//         } else {
//             fetchChartData();
//         }
//         // eslint-disable-next-line
//     }, [payload, toggleTable, post, api_version]);

//     // --- Client-side: filter, sort, paginate table data ---
//     const pageSize = paginationConfig.page_size || 10;
//     const pageNum = paginationConfig.page_num || 1;
//     const searchValue = paginationConfig.search_value || "";
//     const orderBy = paginationConfig.order_by;
//     const orderType = paginationConfig.order_type || "desc";

//     let filtered = rawTableData.results || [];
//     if (searchValue) {
//         filtered = filtered.filter(row =>
//             Object.values(row).some(val =>
//                 String(val || "").toLowerCase().includes(searchValue.toLowerCase())
//             )
//         );
//     }
//     if (orderBy) {
//         filtered = filtered.slice().sort((a, b) => {
//             const aValue = a[orderBy];
//             const bValue = b[orderBy];
//             if (typeof aValue === "number" && typeof bValue === "number") {
//                 return orderType === "asc" ? aValue - bValue : bValue - aValue;
//             } else {
//                 return orderType === "asc"
//                     ? String(aValue).localeCompare(String(bValue))
//                     : String(bValue).localeCompare(String(aValue));
//             }
//         });
//     }
//     const totalRecords = filtered.length;
//     const totalPages = Math.ceil(totalRecords / pageSize);
//     const startIdx = (pageNum - 1) * pageSize;
//     const endIdx = startIdx + pageSize;
//     const paginatedData = filtered.slice(startIdx, endIdx);

//     const createErrorLoaderSection = (type) => (
//         <div className={`error-loader-section ${type}`}>
//             <div className="error-message">Error loading data. Please try again.</div>
//         </div>
//     );
//     const createNoDataLoaderSection = (type) => (
//         <div className={`no-data-loader-section ${type}`}>
//             <div className="no-data-message">No data available</div>
//         </div>
//     );
//     const NoDataAvailableChecker = (data) => {
//         if (!data) return true;
//         if (Array.isArray(data)) return data.length === 0;
//         return Object.keys(data).length === 0;
//     };

//     const handleTableConfigs = (newConfig) => {
//         setPaginationConfig(prev => ({
//             ...prev,
//             ...newConfig
//         }));
//     };

//     // Chart rendering
//     let content;
//     if (isLoading) {
//         content = <Loader loaderType="stackedColumnChartLoader" />;
//     } else if (error) {
//         content = createErrorLoaderSection('stackedColumnChart');
//     } else if (!isLoading && (!chart || !chart.data || chart.data.length === 0 || NoDataAvailableChecker(chart.data))) {
//         content = createNoDataLoaderSection('stackedColumnChart');
//     } else {
//         content = (
//             <Wrapper>
//                 <ErrorBoundary>
//                     <StackedColumnChart
//                         chartClass="section-chart-wrapper"
//                         chartData={chart.data}
//                         chartLabels={chart.label}
//                         chartId={"stackedColumnChart-" + Math.random() * 1000}
//                         showLabels
//                         showLegend
//                         tooltipMaxWidth="auto"
//                     />
//                 </ErrorBoundary>
//             </Wrapper>
//         );
//     }

//     let tableContent = null;
//     if (isLoading) {
//         tableContent = <Loader loaderType="smallTableLoader" />;
//     }
//     // else if (error) {
//     //     tableContent = createErrorLoaderSection('smallTableLoader');
//     // } else if (!filtered.length || NoDataAvailableChecker(filtered)) {
//     //     tableContent = createNoDataLoaderSection('smallTableLoader');
//     // } else if (filtered.length > 0)
//     {
//         tableContent = (
//             <Datatable
//                 tableTitle="Trend Comparison Analysis"
//                 // tableData={paginatedData}
//                 // tableHeader={rawTableData.label}
//                 // tableLength={pageSize}
//                 // tableConfigs={paginationConfig}
//                 // setTableConfigs={handleTableConfigs}
//                 // tablePaginationData={{
//                 //     page_number: pageNum,
//                 //     total_pages: totalPages,
//                 //     total_records: totalRecords,
//                 //     record_end: Math.min(pageNum * pageSize, totalRecords)
//                 // }}
//                 // showPagination={true}
//                 // searchPlaceHolder="Search here..."
//                 tableData={paginatedData}
//                 tableHeader={rawTableData.label}
//                 tableLength={pageSize}
//                 tableConfigs={paginationConfig}
//                 setTableConfigs={handleTableConfigs}
//                 hideBoxShadow={true}
//                 tablePaginationData={{
//                     page_number: pageNum,
//                     total_pages: totalPages,
//                     total_records: totalRecords,
//                     record_end: Math.min(pageNum * pageSize, totalRecords)
//                 }}
//                 searchPlaceHolder="Search here..."
//                 showPagination={true}
//                 error={error}
//                 emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
//             />
//         );
//     }

//     return (
//         <Wrapper>
//             <ErrorBoundary>
//                 <div id="trend_comparison_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
//                     <div className="grid col_span_4">
//                         <div className="section_card_header">
//                             <h2 className="section_card_title">Trend Comparison Analysis</h2>
//                             <div data-html2canvas-ignore={true}>
//                                 <Dropdown
//                                     placeholder="Metric"
//                                     options={valueDropdown}
//                                     className="grid_margin_right"
//                                     value={metric}
//                                     selectedOptions={metric}
//                                     setStatedropdown={setMetric}
//                                 />
//                                 <Dropdown
//                                     placeholder="Dimension"
//                                     options={dimensionDropdown}
//                                     className="grid_margin_right"
//                                     value={dimension}
//                                     selectedOptions={dimension}
//                                     setStatedropdown={setDimension}
//                                 />
//                                 <Dropdown
//                                     placeholder="Granularity"
//                                     options={granularityOptions}
//                                     className="grid_margin_right"
//                                     value={granularity}
//                                     selectedOptions={granularity}
//                                     setStatedropdown={setGranularity}
//                                 />
//                                 <SortOrderButton
//                                     sortOrder={sortOrder}
//                                     toggleSortOrder={toggleSortOrder}
//                                     className="grid_margin_right"
//                                 />
//                                 {/* <Button
//                                     type="primary"
//                                     className="width_fit_content"
//                                     handleClick={() => setToggleTable((prev) => !prev)}
//                                 >
//                                     {toggleTable ? "Chart" : "Table"}
//                                 </Button> */}
//                                 <span className="custom-tooltip-container">
//                                     <button
//                                         type="button"
//                                         className="icon-toggle-btn"
//                                         onClick={() => setToggleTable(prev => !prev)}
//                                         aria-label={toggleTable ? "View Chart" : "View Table"}
//                                     >
//                                         {toggleTable ? (
//                                             <ChartIcon />
//                                         ) : (
//                                             <TableIcon />
//                                         )}
//                                     </button>
//                                     <span className="custom-tooltip">
//                                         {toggleTable ? "View Chart" : "View Table"}
//                                     </span>
//                                 </span>
//                                 <ExportDropdown sectionId="trend_analysis" sectionName="trend_analysis" sectionData={toggleTable ? filtered : chart.data} />
//                             </div>
//                         </div>
//                     </div>
//                     <div className="grid col_span_4">
//                         <div className="section_card_body">
//                             <div className="section_card_chart_wrapper">
//                                 {toggleTable ? tableContent : content}
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </ErrorBoundary>
//         </Wrapper>
//     );
// };

const TrendComparisonAnalysis = () => {
    const api_version = "-v1";

    const [toggleTable, setToggleTable] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");
    // CLIENT-SIDE TABLE DATA
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });
    const [chart, setChart] = useState({ data: [], label: [] });

    const { post, isLoading, error } = useFetch();

    const [granularity, setGranularity] = useState({ value: "year", label: "Yearly" });
    const granularityOptions = [
        { label: 'Yearly', value: 'year' },
        { label: 'Quarterly', value: 'quarter_no' },
        { label: 'Monthly', value: 'month_no' },
    ];

    const [metric, setMetric] = useState({ value: "sales_value", label: "Sales Value" });
    const valueDropdown = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Units", value: "sales_unit" },
        { label: "PPU", value: "ppu" },
        { label: "PPL", value: "ppl" },
        { label: "TDP", value: "tdp" },
        { label: "NumDTotal", value: "numdtotal" },
        { label: "WtDTotal", value: "wtddtotal" },
        { label: "Market Share by sales value", value: "market_share_by_sales_value" },
        { label: "Market Share by Volume", value: "market_share_by_sales_volume" },
        { label: "Market Share by Unit", value: "market_share_by_sales_unit" },
        { label: "Promotional Sales Value%", value: "promotional_sales_value" },
        { label: "Promotional Sales Volume%", value: "promotional_sales_volume" },
        { label: "Promotional Sales Unit%", value: "promotional_sales_unit" },
        { label: "Rate of Sale by Value", value: "valros" },
        { label: "Rate of Sale by Volume", value: "volros" },
        { label: "Rate of Sale by Units", value: "unitros" },
        { label: "Number of SKUs", value: "no_of_sku" },
    ];

    const [dimension, setDimension] = useState({ value: "country", label: "Country" });
    const dimensionDropdown = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" },
    ];

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Use those dates and filters in your payload (useMemo to optimize re-renders)
    const payload = useMemo(
        () => ({
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            },
            section_filter: {
                dimension_dropdown: dimension.value,
                granularity_dropdown: granularity.value,
                value_dropdown: metric.value,
                top_bottom: {
                    sort_by: sortOrder,
                    size: 10
                }
            }
        }),
        [
            startDate,
            endDate,
            prevStartDate,
            prevEndDate,
            secondPrevStartDate,
            secondPrevEndDate,
            dimension,
            granularity,
            metric,
            sortOrder,
            apiGlobalFilters
        ]
    );

    // Toggle sort order function
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Fetch table data (all for client side logic)
    const fetchTableData = async () => {
        const response = await post(`ri/sales/sales-trend-compare-analysis-table${api_version}`, payload);
        if (response) {
            // Defensive: ensure no nulls in data
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v === null || v === undefined ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    // Chart fetch function
    const fetchChartData = async () => {
        const response = await post(`ri/sales/sales-trend-compare-analysis${api_version}`, payload);
        setChart(response);
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable, post, api_version]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    // Chart rendering
    let content;
    if (isLoading) {
        content = <Loader loaderType="stackedColumnChartLoader" />;
    } else if (error) {
        content = createErrorLoaderSection('stackedColumnChart');
    } else if (!isLoading && (!chart || !chart.data || chart.data.length === 0 || NoDataAvailableChecker(chart.data))) {
        content = createNoDataLoaderSection('stackedColumnChart');
    } else {
        content = (
            <Wrapper>
                <ErrorBoundary>
                    <StackedColumnChart
                        chartClass="section-chart-wrapper"
                        chartData={chart.data}
                        chartLabels={chart.label}
                        chartId={"stackedColumnChart-" + Math.random() * 1000}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                    />
                </ErrorBoundary>
            </Wrapper>
        );
    }

    let tableContent = null;
    if (isLoading) {
        tableContent = <Loader loaderType="smallTableLoader" />;
    }
    // else if (error) {
    //     tableContent = createErrorLoaderSection('smallTableLoader');
    // } else if (!filtered.length || NoDataAvailableChecker(filtered)) {
    //     tableContent = createNoDataLoaderSection('smallTableLoader');
    // } else if (filtered.length > 0)
    {
        tableContent = (
            <Datatable
                tableTitle="Trend Comparison Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="trend_comparison_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Trend Comparison Analysis</h2>
                            <div data-html2canvas-ignore={true}>
                                <Dropdown
                                    placeholder="Metric"
                                    options={valueDropdown}
                                    className="grid_margin_right"
                                    value={metric}
                                    selectedOptions={metric}
                                    setStatedropdown={setMetric}
                                />
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionDropdown}
                                    className="grid_margin_right"
                                    value={dimension}
                                    selectedOptions={dimension}
                                    setStatedropdown={setDimension}
                                />
                                <Dropdown
                                    placeholder="Granularity"
                                    options={granularityOptions}
                                    className="grid_margin_right"
                                    value={granularity}
                                    selectedOptions={granularity}
                                    setStatedropdown={setGranularity}
                                />
                                <SortOrderButton
                                    sortOrder={sortOrder}
                                    toggleSortOrder={toggleSortOrder}
                                    // className="grid_margin_right"
                                    className="section_card_header_sort_btn"

                                />
                                {/* <Button
                                    type="primary"
                                    className="width_fit_content"
                                    handleClick={() => setToggleTable((prev) => !prev)}
                                >
                                    {toggleTable ? "Chart" : "Table"}
                                </Button> */}
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable(prev => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                    >
                                        {toggleTable ? (
                                            <ChartIcon />
                                        ) : (
                                            <TableIcon />
                                        )}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown sectionId="trend_comparison_section" sectionName="Trend Comparison Analysis" sectionData={toggleTable ? filtered : chart.data} />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? tableContent : content}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};


const TrendAnalysis = () => {
    const api_version = '-v1';

    // Chart & Table state
    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    // Toggles
    const [sortOrder, setSortOrder] = useState("desc");
    const [toggleTopBottom, setToggleTopBottom] = useState(true);
    const [toggleTable, setToggleTable] = useState(false);

    // Fetch hook
    const { post, isLoading, error } = useFetch();

    // Dropdowns state & options
    const tooltipOptions = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Units", value: "sales_unit" },
        { label: "PPU", value: "ppu" },
        { label: "PPL", value: "ppl" },
        { label: "TDP", value: "tdp" },
        { label: "NumDTotal", value: "numdtotal" },
        { label: "WtDTotal", value: "wtddtotal" },
        { label: "Market Share by sales value", value: "market_share_by_sales_value" },
        { label: "Market Share by Volume", value: "market_share_by_sales_volume" },
        { label: "Market Share by Unit", value: "market_share_by_sales_unit" },
        { label: "Promotional Sales Value%", value: "promotional_sales_value" },
        { label: "Promotional Sales Volume%", value: "promotional_sales_volume" },
        { label: "Promotional Sales Unit%", value: "promotional_sales_unit" },
        { label: "Rate of Sale by Value", value: "valros" },
        { label: "Rate of Sale by Volume", value: "volros" },
        { label: "Rate of Sale by Units", value: "unitros" },
        { label: "Number of SKUs", value: "no_of_sku" },
    ];

    // State for the two dropdowns
    const [tooltip, setTooltip] = useState({ value: 'sales_value', label: 'Sales Value' });
    const [valueMetric, setValueMetric] = useState({ value: 'sales_volume', label: 'Sales Volume' });

    // Filter options so that the selected value in one is not available in the other
    const filteredTooltipOptions = useMemo(() =>
        tooltipOptions.filter(opt => opt.value !== valueMetric.value),
        [valueMetric.value]
    );
    const filteredValueOptions = useMemo(() =>
        tooltipOptions.filter(opt => opt.value !== tooltip.value),
        [tooltip.value]
    );

    // When user selects a value in one dropdown, if both match, auto-change the other to the first allowed option
    useEffect(() => {
        if (tooltip.value === valueMetric.value) {
            const nextValue = tooltipOptions.find(opt => opt.value !== tooltip.value);
            if (nextValue) setValueMetric(nextValue);
        }
        // eslint-disable-next-line
    }, [tooltip]);

    useEffect(() => {
        if (tooltip.value === valueMetric.value) {
            const nextValue = tooltipOptions.find(opt => opt.value !== valueMetric.value);
            if (nextValue) setTooltip(nextValue);
        }
        // eslint-disable-next-line
    }, [valueMetric]);

    const valueOptions = [...tooltipOptions];

    const [dimension, setDimension] = useState({ value: 'manufacture', label: 'Manufacture' });
    const dimensionOptions = [
        { value: 'manufacture', label: 'Manufacture' },
        { value: 'brand', label: 'Brand' },
        { value: 'category', label: 'Category' },
        { value: 'segment', label: 'Segment' },
        { value: 'form', label: 'Form' },
        { value: 'variant', label: 'Variant' },
        { value: 'pack_size', label: 'Pack Size' },
        { value: 'promotion', label: 'Promotion' },
        { value: 'country', label: 'Country' },
        { value: 'store_type', label: 'Store Type' },
        { value: 'sub_category', label: 'Sub Category' },
        { value: 'sku', label: 'SKU' },
    ];

    const [granularity, setGranularity] = useState({ value: 'year', label: 'Yearly' });
    const granularityOptions = [
        { value: 'year', label: 'Yearly' },
        { value: 'quarter_no', label: 'Quarterly' },
        { value: 'month_no', label: 'Monthly' },
    ];

    // 1. Redux dates from DatePicker
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Memoize payload so it updates on any filter/date change
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            tooltip_dropdown: tooltip.value,
            value_dropdown: valueMetric.value,
            dimension_dropdown: dimension.value,
            granularity_dropdown: granularity.value,
            top_bottom: {
                sort_by: sortOrder,
                size: 5
            }
        }
    }), [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters,
        tooltip.value,
        valueMetric.value,
        dimension.value,
        granularity.value,
        sortOrder
    ]);

    // Toggle sort order
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Fetch chart or table
    const fetchTableData = async () => {
        const response = await post(
            `ri/sales/sales-trend-analysis-table${api_version}`,
            payload
        );
        if (response) {
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v === null || v === undefined ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    const fetchChartData = async () => {
        const response = await post(
            `ri/sales/sales-trend-analysis${api_version}`,
            payload
        );
        setChart(response);
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable, api_version, post]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    // Pagination/search/sort handler for table (client-side)
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart || !chart.chartData || chart.chartData.length === 0 || NoDataAvailableChecker(chart.chartData))
            return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <CustomBarChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.chartData}
                    chartLabels={chart.chartLabels}
                    chartId={'stackedColumnChart-' + Math.random() * 1000}
                    showLabels
                    showLegend
                    tooltipMaxWidth="auto"
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        return (
            <Datatable
                tableTitle="Trend Analysis"
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="trend_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Trend Analysis</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Tooltip"
                                options={filteredTooltipOptions}
                                className="grid_margin_right"
                                value={tooltip}
                                selectedOptions={tooltip}
                                setStatedropdown={setTooltip}
                            />
                            <Dropdown
                                placeholder="Value"
                                options={filteredValueOptions}
                                className="grid_margin_right"
                                value={valueMetric}
                                selectedOptions={valueMetric}
                                setStatedropdown={setValueMetric}
                            />
                            <Dropdown
                                placeholder="Dimension"
                                options={dimensionOptions}
                                className="grid_margin_right"
                                value={dimension}
                                selectedOptions={dimension}
                                setStatedropdown={setDimension}
                            />
                            <Dropdown
                                placeholder="Granularity"
                                options={granularityOptions}
                                className="grid_margin_right"
                                value={granularity}
                                selectedOptions={granularity}
                                setStatedropdown={setGranularity}
                            />
                            <SortOrderButton
                                sortOrder={sortOrder}
                                toggleSortOrder={toggleSortOrder}
                                // className="grid_margin_right"
                                className="section_card_header_sort_btn"
                            />
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown
                                sectionId="trend_analysis_section"
                                sectionName="Trend Analysis"
                                sectionData={toggleTable ? filtered : chart.chartData}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const SalesOverview = () => {
    const api_version = "-v1";
    const apiEndpoints = [
        { key: "promotion", url: `ri/sales/sales-sales-overview-pie-chart-promotion${api_version}`, chartType: "Pie" },
        { key: "category", url: `ri/sales/sales-sales-overview-pie-chart-category${api_version}`, chartType: "Pie" },
        { key: "segment", url: `ri/sales/sales-sales-overview-bar-chart-segment${api_version}`, chartType: "Bar" },
        { key: "manufacturer", url: `ri/sales/sales-sales-overview-bar-chart-manufacturer${api_version}`, chartType: "Bar" },
        { key: "brand", url: `ri/sales/sales-sales-overview-bar-chart-brand${api_version}`, chartType: "Bar" },
        { key: "variant", url: `ri/sales/sales-sales-overview-bar-chart-variant${api_version}`, chartType: "Bar" },
        { key: "pack Size", url: `ri/sales/sales-sales-overview-bar-chart-pack-size${api_version}`, chartType: "Bar" },
    ];

    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = React.useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    const [metricDropdown, setMetricDropdown] = useState({
        value: "sales_value",
        label: "Sales Value"
    });
    const [sortOrder, setSortOrder] = useState("desc");

    // 3. Use those dates and global filters in your payload
    const [payload, setPayload] = useState({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: "sales_value",
            top_bottom: {
                sort_by: "desc",
                size: 5
            }
        }
    });

    // 4. Update payload when Redux date values or global filters change
    useEffect(() => {
        setPayload(prevPayload => ({
            ...prevPayload,
            global_filter: {
                ...prevPayload.global_filter,
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            }
        }));
        // eslint-disable-next-line
    }, [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const primaryDropdownOptions = [
        { label: "Sales Value", value: "sales_value" },
        { label: "Sales Volume", value: "sales_volume" },
        { label: "Sales Unit", value: "sales_unit" },
    ];

    const metricDropDownHandler = (e) => {
        const selectedValue = e?.value || "sales_value";
        setMetricDropdown(e || {});
        setPayload((prevPayload) => ({
            ...prevPayload,
            section_filter: {
                ...prevPayload.section_filter,
                value_dropdown: selectedValue
            }
        }));
    };

    // ChartCard sorts data locally according to sortOrder
    const ChartCard = ({ endpoint, sortOrder }) => {
        const { key, url, chartType } = endpoint;
        const [data, setData] = useState(null);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState(null);
        const { post } = useFetch();

        useEffect(() => {
            setIsLoading(true);
            post(url, payload)
                .then(response => {
                    setData(response?.data || response);
                    setError(null);
                })
                .catch(err => {
                    setError(err);
                    setData(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }, [url, payload]);

        // Sort for Bar charts based on sortOrder
        let sortedData = data;
        if (chartType === "Bar" && Array.isArray(data)) {
            sortedData = [...data].sort((a, b) => {
                const valA = Number(a.value);
                const valB = Number(b.value);
                return sortOrder === "desc" ? valB - valA : valA - valB;
            });
        }

        return (
            // <WrapperCard className="p_10" style={{ maxHeight: "300px" }}>
            <WrapperCard className="p_10" style={{
                maxHeight: "300px",
                // minHeight: "240px",           // Ensure enough height for border and content
                // maxHeight: "unset",           // Remove the fixed height restriction
                // boxSizing: "border-box",      // Make sure border and padding included in height
                paddingBottom: "16px",        // Add bottom padding for border visibility
                // margin: "0 8px",              // Space between cards if needed
                border: "1px solid #eee",     // Ensure border is visible
                borderRadius: "12px",         // Match Sales Performance style
                background: "#fff"            // Ensure white background
            }}>
                <div className="header">
                    <h5 className="card_title">
                        <b>{key.charAt(0).toUpperCase() + key.slice(1)}</b>
                    </h5>
                </div>
                {isLoading ? (
                    createLoaderSection("chartCard")
                ) : error ? (
                    createErrorLoaderSection("chartCard")
                ) : !data || data.length === 0 ? (
                    createNoDataLoaderSection("chartCard")
                ) : chartType === "Pie" ? (
                    <Pie
                        chartData={data}
                        chartId={`${key}-chart-${Math.floor(Math.random() * 1000)}`}
                        chartClass="section-chart-wrapper"
                    />
                ) : (
                    <BarChart
                        chartData={sortedData}
                        chartLabels={[{ label: metricDropdown.label, value: "value" }]}
                        chartId={`${key}-chart-${Math.floor(Math.random() * 1000)}`}
                        chartClass="section-chart-wrapper"
                        // variant="centeredLegend"
                        showMatrixLegend={true}
                    />
                )}
            </WrapperCard>
        );
    };

    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPayload((prevPayload) => ({
            ...prevPayload,
            section_filter: {
                ...prevPayload.section_filter,
                top_bottom: {
                    ...prevPayload.section_filter.top_bottom,
                    sort_by: newSortOrder
                }
            }
        }));
    };

    return (
        <Wrapper>
            <WrapperCard>
                <WrapperCard.HeaderWrap>
                    <WrapperCard.Header>
                        <WrapperCard.Header.Title>Sales Overview</WrapperCard.Header.Title>
                    </WrapperCard.Header>
                    <div data-html2canvas-ignore={true}>
                        <Dropdown
                            placeholder="Select"
                            options={primaryDropdownOptions}
                            value={metricDropdown}
                            selectedOptions={metricDropdown}
                            setStatedropdown={metricDropDownHandler}
                            className="grid_margin_right dropdownpositionchange"

                        />
                        <SortOrderButton
                            sortOrder={sortOrder}
                            toggleSortOrder={toggleSortOrder}
                            // className="sort-order-button"
                            className="section_card_header_sort_btn dropdownpositionchange"

                        />
                        {/* <ExportDropdown sectionId="sales_overview" sectionName="Sales Overview" sectionData={[]} /> */}
                    </div>
                </WrapperCard.HeaderWrap>

                <br />
                <WrapperCard.Body>
                    <Slider>
                        {apiEndpoints.map((endpoint) => (
                            <SwiperSlide key={endpoint.key}>
                                <ChartCard endpoint={endpoint} sortOrder={sortOrder} />
                            </SwiperSlide>
                        ))}
                    </Slider>
                </WrapperCard.Body>
            </WrapperCard>
        </Wrapper>
    );
};

const SalesPerformance = () => {
    const api_version = "-v1";
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);

    // Get date values from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Map UI filter keys to API keys and build payload
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // Memoize payload so it updates on any filter/date change
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters,
        },
    }), [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const { post } = useFetch();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch data whenever payload changes (dates or filters)
    useEffect(() => {
        setIsLoading(true);
        post(`ri/sales/sales-sales-performance${api_version}`, payload)
            .then(response => {
                setData(response?.data || response);
                setError(null);
            })
            .catch(err => {
                setError(err);
                setData(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [payload, post, api_version]);

    let content;
    if (isLoading) {
        content = createLoaderSection('cardLoader', "Sales Performance", 'chartCard');
    } else if (error) {
        content = createErrorLoaderSection('cardType');
    } else if (!isLoading && (!data || data.length === 0 || NoDataAvailableChecker(data))) {
        content = createNoDataLoaderSection('cardType');
    } else {
        const CARD_DATA = data || [];
        content = (
            <Slider sliderTitle="Sales Performance">
                {CARD_DATA.map((data, index) => (
                    <SwiperSlide key={index}>
                        <Card
                            module="summary"
                            section="summary_distribution_performance"
                            variant={"chartCard"}
                            cardTitle={data.label}
                            currentValue={data.currentValue}
                            previousValue={data.previousValue}
                            chartType={"Line"}
                            chartData={data.subData?.data}
                            chartDataLabels={data.subData?.dimension}
                        />
                    </SwiperSlide>
                ))}
            </Slider>
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {content}
            </ErrorBoundary>
        </Wrapper>
    );
};

const PremiumIndexAnalysis = () => {
    const api_version = '-v1';

    // Chart & Table state
    const [chart, setChart] = useState({
        chartData: [],
        chartLabels: [
            { label: "Sales Value", value: "salesValue" },
            { label: "Sales Volume", value: "volume" },
            { label: "Premium Index", value: "premium" }
        ]
    });
    // CLIENT-SIDE TABLE DATA
    const [rawTableData, setRawTableData] = useState({ results: [], label: [], page: {} });
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    // Toggles
    const [sortOrder, setSortOrder] = useState("desc");
    const [toggleTable, setToggleTable] = useState(false);

    // Fetch hook
    const { post, isLoading, error } = useFetch();

    // Dropdown state & options
    const [dimension, setDimension] = useState({ value: 'brand', label: 'Brand' });
    const dimensionOptions = [
        { label: "Country", value: "country" },
        { label: "Store Type", value: "store_type" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Segment", value: "segment" },
        { label: "Form", value: "form" },
        { label: "Manufacture", value: "manufacture" },
        { label: "Brand", value: "brand" },
        { label: "Variant", value: "variant" },
        { label: "Pack Size", value: "pack_size" },
        { label: "Promotion", value: "promotion" },
        { label: "SKU", value: "sku" }
    ];
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    // 2. Map UI filter keys to API keys (as in your filterKeyMap)

    // 3. Transform selectedFilters to API format (memoized)
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);
    // 1. Get date values from Redux (DatePicker)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // 2. Use those dates in your payload (useMemo to optimize re-renders)
    const payload = useMemo(
        () => ({
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters,
            },
            section_filter: {
                dimension_dropdown: dimension.value,
                top_bottom: {
                    sort_by: sortOrder,
                    size: 5
                }
            }
        }),
        [dimension, sortOrder, startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate, apiGlobalFilters]
    );

    // Toggle sort order function
    const toggleSortOrder = () => {
        const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
        setSortOrder(newSortOrder);
        setPaginationConfig(prev => ({
            ...prev,
            order_type: newSortOrder
        }));
    };

    // Fetch chart or table
    const fetchTableData = async () => {
        const response = await post(
            `/ri/sales/sales-premium-index-analysis-table${api_version}`,
            payload
        );
        if (response) {
            const safeData = (response.data || []).map(row => {
                const safeRow = {};
                Object.entries(row).forEach(([k, v]) => {
                    safeRow[k] = v === null || v === undefined ? "" : v;
                });
                return safeRow;
            });
            setRawTableData({
                results: safeData,
                label: response.label || [],
                page: response.pagination || {}
            });
        }
    };

    const fetchChartData = async () => {
        const response = await post(
            `ri/sales/sales-premium-index-analysis${api_version}`,
            payload
        );
        setChart({
            chartData: response.chartData || [],
            chartLabels: [
                { label: "Market Share by sales value", value: "market_share_by_sales_value" },
                { label: "Market Share by volume", value: "market_share_by_sales_volume" },
                { label: "Premium Index", value: "premium" }
            ]
        });
    };

    useEffect(() => {
        if (toggleTable) {
            fetchTableData();
        } else {
            fetchChartData();
        }
        // eslint-disable-next-line
    }, [payload, toggleTable, api_version, post, selectedFilters, apiGlobalFilters]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    // Pagination/search/sort handler for table (client-side)
    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!chart || !chart.chartData || chart.chartData.length === 0 || NoDataAvailableChecker(chart.chartData))
            return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <PremiumIndexChart
                    chartClass="section-chart-wrapper"
                    chartData={chart.chartData}
                    chartLabels={chart.chartLabels}
                    chartId={'premiumIndexChart-' + Math.random() * 1000}
                />
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        // if (error) return createErrorLoaderSection('smallTableLoader');
        // if (!filtered.length || NoDataAvailableChecker(filtered))
        //     return createNoDataLoaderSection('smallTableLoader');
        return (
            <Datatable
                tableTitle="Premium Index Analysis"
                // tableData={paginatedData}
                // tableHeader={rawTableData.label}
                // tableLength={pageSize}
                // tableConfigs={paginationConfig}
                // setTableConfigs={handleTableConfigs}
                // tablePaginationData={{
                //     page_number: pageNum,
                //     total_pages: totalPages,
                //     total_records: totalRecords,
                //     record_end: Math.min(pageNum * pageSize, totalRecords)
                // }}
                // showPagination={true}
                // searchPlaceHolder="Search brands..."
                tableData={paginatedData}
                tableHeader={rawTableData.label}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                hideBoxShadow={true}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search here..."
                showPagination={true}
                error={error}
                emptyStateComponent={createNoDataLoaderSection("smallTableLoader")}
            />
        );
    };

    return (
        <ErrorBoundary>
            <div id="premium_index_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                <div className="grid col_span_4">
                    <div className="section_card_header">
                        <h2 className="section_card_title">Premium Index Analysis</h2>
                        <div data-html2canvas-ignore>
                            <Dropdown
                                placeholder="Dimension"
                                options={dimensionOptions}
                                className="grid_margin_right"
                                value={dimension}
                                selectedOptions={dimension}
                                setStatedropdown={setDimension}
                            />
                            <SortOrderButton
                                sortOrder={sortOrder}
                                toggleSortOrder={toggleSortOrder}
                                // className="grid_margin_right"
                                className="section_card_header_sort_btn"

                            />
                            {/* <Button
                                type="primary"
                                className="width_fit_content"
                                handleClick={() => setToggleTable(prev => !prev)}
                            >
                                {toggleTable ? "Chart" : "Table"}
                            </Button> */}
                            <span className="custom-tooltip-container">
                                <button
                                    type="button"
                                    className="icon-toggle-btn"
                                    onClick={() => setToggleTable(prev => !prev)}
                                    aria-label={toggleTable ? "View Chart" : "View Table"}
                                >
                                    {toggleTable ? (
                                        <ChartIcon />
                                    ) : (
                                        <TableIcon />
                                    )}
                                </button>
                                <span className="custom-tooltip">
                                    {toggleTable ? "View Chart" : "View Table"}
                                </span>
                            </span>
                            <ExportDropdown sectionId="premium_index_analysis_section" sectionName="premium index analysis" sectionData={toggleTable ? filtered : chart.chartData} />
                        </div>
                    </div>
                </div>
                <div className="grid col_span_4">
                    <div className="section_card_body">
                        <div className="section_card_chart_wrapper">
                            {toggleTable ? renderTable() : renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const Sales = () => {
    // Map panel state and logic (copied and adapted from Distribution/CrossSource)
    const [showMapPanel, setShowMapPanel] = useState(false);
    const [mapGeoJson, setMapGeoJson] = useState(null);
    const [mapLoading, setMapLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [hoverData, setHoverData] = useState({});
    const [hoverLoading, setHoverLoading] = useState(false);
    const [hoverError, setHoverError] = useState(null);

    // Get date and global filters from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);
    const mapPayload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        }
    }), [startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate, apiGlobalFilters]);
    const { post } = useFetch();

    // Listen for custom event from sidebar map button
    useEffect(() => {
        const handler = () => setShowMapPanel(true);
        window.addEventListener('open-map-panel', handler);
        return () => window.removeEventListener('open-map-panel', handler);
    }, []);

    // Fetch map data and hover data when map panel opens
    useEffect(() => {
        if (!showMapPanel) return;
        setMapLoading(true);
        setMapError(null);
        setHoverLoading(true);
        setHoverError(null);
        // Fetch map coordinates
        post('ri/map-coordinates', mapPayload)
            .then(response => {
                // Convert response to GeoJSON
                const features = Array.isArray(response)
                    ? response.map(item => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [item.longitude, item.latitude]
                        },
                        properties: {
                            ...item,
                            name: item.country_name || item.country_code
                        }
                    }))
                    : [];
                setMapGeoJson({ type: 'FeatureCollection', features });

                // Prepare country_ids for hover API
                const countryIds = features.map(f => f.properties.country_id).filter(Boolean);
                if (countryIds.length > 0) {
                    // Call hover API for all countries
                    const hoverPayload = {
                        global_filter: {
                            date_filter: mapPayload.global_filter.date_filter,
                            filter: {
                                ...mapPayload.global_filter.filter,
                                country_id: countryIds
                            }
                        }
                    };
                    post('ri/map-hover', hoverPayload)
                        .then(hoverResponse => {
                            // Store hover data by country_id
                            if (Array.isArray(hoverResponse)) {
                                const hoverDataObj = {};
                                hoverResponse.forEach(d => {
                                    if (d.country_id) hoverDataObj[d.country_id] = d;
                                });
                                setHoverData(hoverDataObj);
                            }
                        })
                        .catch(err => {
                            setHoverError(err);
                        })
                        .finally(() => setHoverLoading(false));
                } else {
                    setHoverData({});
                    setHoverLoading(false);
                }
            })
            .catch(err => {
                setMapError(err);
                setMapGeoJson(null);
                setHoverLoading(false);
            })
            .finally(() => setMapLoading(false));
    }, [showMapPanel, post, mapPayload]);

    // Hover handler for map markers
    const handleMarkerHover = (properties) => {
        if (!properties || !properties.country_id) return;
        setHoverLoading(true);
        setHoverError(null);
        // Add country_id to filter for hover
        const hoverPayload = {
            global_filter: {
                date_filter: mapPayload.global_filter.date_filter,
                filter: {
                    ...mapPayload.global_filter.filter,
                    country_id: [properties.country_id]
                }
            }
        };
        post('ri/map-hover', hoverPayload)
            .then(response => {
                // Response is array, find matching country_id
                if (Array.isArray(response)) {
                    const data = response.find(d => d.country_id === properties.country_id);
                    setHoverData(prev => ({ ...prev, [properties.country_id]: data }));
                }
            })
            .catch(err => {
                setHoverError(err);
            })
            .finally(() => setHoverLoading(false));
    };

    return (
        <Wrapper>
            <div className="dashboard-container">
                {/* LEFT: Map (only visible if showMapPanel) */}
                <div
                    className="dashboard-leftside"
                    style={
                        showMapPanel
                            // ? { width: 400, minWidth: 320, maxWidth: 440, transition: 'width 0.3s' }
                            // : { width: 0, minWidth: 0, maxWidth: 0, transition: 'width 0.3s', overflow: 'hidden', padding: 0 }
                    
                            ? { flexBasis: 400, minWidth: 320, maxWidth: 440, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)' }
                            : { flexBasis: 0, minWidth: 0, maxWidth: 0, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', padding: 0 }
                        }
                >
                    {showMapPanel && (
                        <div
                            className="dashboard-left map-panel-ubuntu map-panel-custom"

                        >
                            {/* Header */}
                            <div className="map-panel-header">
                                <span className="map-panel-title">UAE</span>
                                <button
                                    onClick={() => setShowMapPanel(false)}
                                    className="map-panel-close-btn"
                                    aria-label="Close Map"
                                >
                                    ×
                                </button>
                            </div>
                            {/* Map */}
                            <div className="map-panel-map-container">
                                {(mapLoading || hoverLoading) && (
                                      <div className="map-panel-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
                                        <img
                                            src={require('../../../assets/images/gcc-map-loader.png')}
                                            alt="Loading map..."
                                            style={{ width: 240, height: 'auto', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                                {(mapError || hoverError) && (
                                    <div className="map-panel-error">Error loading map data</div>
                                )}
                                {!mapLoading && !hoverLoading && mapGeoJson && (
                                    <MapComponent
                                        geoJson={mapGeoJson}
                                        mapLevel={1}
                                        northPoint={["31.273904, 34.525594"]}
                                        southPoint={["60.695581, 13.951499"]}
                                        centerPoint={["44.556137,23.785713"]}
                                        zoomLevel={2.5}
                                        onChange={() => { }}
                                        toolTipValues={["country_name", "country_code"]}
                                        toolTipLabels={["Country Name", "Country Code"]}
                                        growthDegrowth={null}
                                        onMarkerHover={handleMarkerHover}
                                        hoverData={hoverData}
                                    />
                                )}
                            </div>
                            {/* Legend */}
                            <div className="map-panel-legend">
                                <span className="map-panel-legend-item">
                                    <span className="legend-dot green"></span>
                                    Sales Value Growth
                                </span>
                                <span className="map-panel-legend-item">
                                    <span className="legend-dot red"></span>
                                    Sales Value De-Growth
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: Dashboard content */}
                <div className="dashboard-right"
    style={{
        flex: 1,
        minWidth: 0
    }}>
                    <SelectedGlobalFilter />
                    <div className="content">
                        <div className="content_section">
                            <PageHeader pageTitle={"Sales"} />
                            <div className="page_content">
                                <SalesPerformance />
                                <SalesOverview />
                                <TrendAnalysis />
                                <TrendComparisonAnalysis />
                                <WaterfallAnalysis />
                                <PremiumIndexAnalysis />
                                <MarketShareGrowth />
                                <CategoryDeepDiveAnalysis />
                                <PromotionEffectiveAnalysis />
                                <PriceImpactAnalysis />
                                <SalesDeepDiveAnalysis />
                                <SummaryTable />
                                <CompareAnalysis />
                                {/* <GrowthCompareAnalysis/> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </Wrapper>
    )
}

export default Sales